/**
 * La conferma prima di un'azione che non si annulla (§1.12): l'AlertDialog di
 * shadcn al posto di `window.confirm`, con la stessa comodità d'uso.
 *
 *   const [chiedi, conferma] = useConferma();
 *   if (!(await chiedi({ titolo: 'Eliminare il gruppo?', azione: 'Elimina' }))) return;
 *   …
 *   return <>{…}{conferma}</>;
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface Domanda {
  titolo: string;
  testo?: ReactNode;
  /** Etichetta del pulsante che conferma: il verbo dell'azione. */
  azione: string;
  /** Pulsante rosso: per ciò che cancella o toglie l'accesso. */
  distruttiva?: boolean;
}

export function useConferma(): [(domanda: Domanda) => Promise<boolean>, ReactNode] {
  const [domanda, setDomanda] = useState<Domanda | null>(null);
  const risolvi = useRef<((si: boolean) => void) | null>(null);

  const chiedi = useCallback(
    (d: Domanda) =>
      new Promise<boolean>((resolve) => {
        risolvi.current = resolve;
        setDomanda(d);
      }),
    [],
  );

  const chiudi = (si: boolean) => {
    risolvi.current?.(si);
    risolvi.current = null;
    setDomanda(null);
  };

  const dialogo = (
    <AlertDialog open={domanda !== null} onOpenChange={(aperto) => !aperto && chiudi(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{domanda?.titolo}</AlertDialogTitle>
          {domanda?.testo && <AlertDialogDescription>{domanda.testo}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction
            variant={domanda?.distruttiva ? 'destructive' : 'default'}
            onClick={() => chiudi(true)}
          >
            {domanda?.azione}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [chiedi, dialogo];
}
